package top.wcpe.allincore.ui.runtime.theme

import top.wcpe.allincore.ui.runtime.element.FontStyle
import kotlin.jvm.JvmName

/**
 * 跨控件共享的设计令牌核心。
 *
 * 控件专属令牌继续放在各自 TokenHolder；本类只保存色板、状态层、排版、形状、
 * 共享几何和装饰效果等跨控件语义。
 */
@Suppress("MagicNumber", "TooManyFunctions")
data class DesignTokens(
    val colors: ColorTokens = ColorTokens(),
    val stateLayers: StateLayerTokens = StateLayerTokens(),
    val typography: TypographyTokens = TypographyTokens(),
    val shape: ShapeTokens = ShapeTokens(),
    val geometry: SharedGeometryTokens = SharedGeometryTokens(),
    val decoration: DecorationTokens = DecorationTokens(),
) {
    @get:JvmName("primary")
    val primary: Int get() = colors.primary

    @get:JvmName("onPrimary")
    val onPrimary: Int get() = colors.onPrimary

    @get:JvmName("primaryContainer")
    val primaryContainer: Int get() = colors.primaryContainer

    @get:JvmName("onPrimaryContainer")
    val onPrimaryContainer: Int get() = colors.onPrimaryContainer

    @get:JvmName("inversePrimary")
    val inversePrimary: Int get() = colors.inversePrimary

    @get:JvmName("secondary")
    val secondary: Int get() = colors.secondary

    @get:JvmName("onSecondary")
    val onSecondary: Int get() = colors.onSecondary

    @get:JvmName("secondaryContainer")
    val secondaryContainer: Int get() = colors.secondaryContainer

    @get:JvmName("onSecondaryContainer")
    val onSecondaryContainer: Int get() = colors.onSecondaryContainer

    @get:JvmName("tertiary")
    val tertiary: Int get() = colors.tertiary

    @get:JvmName("onTertiary")
    val onTertiary: Int get() = colors.onTertiary

    @get:JvmName("tertiaryContainer")
    val tertiaryContainer: Int get() = colors.tertiaryContainer

    @get:JvmName("onTertiaryContainer")
    val onTertiaryContainer: Int get() = colors.onTertiaryContainer

    @get:JvmName("error")
    val error: Int get() = colors.error

    @get:JvmName("onError")
    val onError: Int get() = colors.onError

    @get:JvmName("errorContainer")
    val errorContainer: Int get() = colors.errorContainer

    @get:JvmName("onErrorContainer")
    val onErrorContainer: Int get() = colors.onErrorContainer

    @get:JvmName("successColor")
    val successColor: Int get() = colors.successColor

    @get:JvmName("onSuccess")
    val onSuccess: Int get() = colors.onSuccess

    @get:JvmName("warningColor")
    val warningColor: Int get() = colors.warningColor

    @get:JvmName("onWarning")
    val onWarning: Int get() = colors.onWarning

    @get:JvmName("infoColor")
    val infoColor: Int get() = colors.infoColor

    @get:JvmName("onInfo")
    val onInfo: Int get() = colors.onInfo

    @get:JvmName("background")
    val background: Int get() = colors.background

    @get:JvmName("onBackground")
    val onBackground: Int get() = colors.onBackground

    @get:JvmName("surface")
    val surface: Int get() = colors.surface

    @get:JvmName("onSurface")
    val onSurface: Int get() = colors.onSurface

    @get:JvmName("surfaceVariant")
    val surfaceVariant: Int get() = colors.surfaceVariant

    @get:JvmName("onSurfaceVariant")
    val onSurfaceVariant: Int get() = colors.onSurfaceVariant

    @get:JvmName("surfaceContainerLowest")
    val surfaceContainerLowest: Int get() = colors.surfaceContainerLowest

    @get:JvmName("surfaceContainerLow")
    val surfaceContainerLow: Int get() = colors.surfaceContainerLow

    @get:JvmName("surfaceContainer")
    val surfaceContainer: Int get() = colors.surfaceContainer

    @get:JvmName("surfaceContainerHigh")
    val surfaceContainerHigh: Int get() = colors.surfaceContainerHigh

    @get:JvmName("surfaceContainerHighest")
    val surfaceContainerHighest: Int get() = colors.surfaceContainerHighest

    @get:JvmName("surfaceTint")
    val surfaceTint: Int get() = colors.surfaceTint

    @get:JvmName("inverseSurface")
    val inverseSurface: Int get() = colors.inverseSurface

    @get:JvmName("inverseOnSurface")
    val inverseOnSurface: Int get() = colors.inverseOnSurface

    @get:JvmName("outline")
    val outline: Int get() = colors.outline

    @get:JvmName("outlineVariant")
    val outlineVariant: Int get() = colors.outlineVariant

    @get:JvmName("shadow")
    val shadow: Int get() = colors.shadow

    @get:JvmName("scrim")
    val scrim: Int get() = colors.scrim

    @get:JvmName("stateLayerHoverOpacity")
    val stateLayerHoverOpacity: Float get() = stateLayers.hoverOpacity

    @get:JvmName("stateLayerFocusOpacity")
    val stateLayerFocusOpacity: Float get() = stateLayers.focusOpacity

    @get:JvmName("stateLayerPressedOpacity")
    val stateLayerPressedOpacity: Float get() = stateLayers.pressedOpacity

    @get:JvmName("stateLayerDraggedOpacity")
    val stateLayerDraggedOpacity: Float get() = stateLayers.draggedOpacity

    @get:JvmName("disabledOpacity")
    val disabledOpacity: Float get() = stateLayers.disabledOpacity

    @get:JvmName("disabledContentOpacity")
    val disabledContentOpacity: Float get() = stateLayers.disabledContentOpacity

    @get:JvmName("typographyDisplayLargeSize")
    val typographyDisplayLargeSize: Float get() = typography.displayLargeSize

    @get:JvmName("typographyDisplayLargeLineHeight")
    val typographyDisplayLargeLineHeight: Float get() = typography.displayLargeLineHeight

    @get:JvmName("typographyDisplayLargeLetterSpacing")
    val typographyDisplayLargeLetterSpacing: Float get() = typography.displayLargeLetterSpacing

    @get:JvmName("typographyDisplayMediumSize")
    val typographyDisplayMediumSize: Float get() = typography.displayMediumSize

    @get:JvmName("typographyDisplayMediumLineHeight")
    val typographyDisplayMediumLineHeight: Float get() = typography.displayMediumLineHeight

    @get:JvmName("typographyDisplayMediumLetterSpacing")
    val typographyDisplayMediumLetterSpacing: Float get() = typography.displayMediumLetterSpacing

    @get:JvmName("typographyDisplaySmallSize")
    val typographyDisplaySmallSize: Float get() = typography.displaySmallSize

    @get:JvmName("typographyDisplaySmallLineHeight")
    val typographyDisplaySmallLineHeight: Float get() = typography.displaySmallLineHeight

    @get:JvmName("typographyDisplaySmallLetterSpacing")
    val typographyDisplaySmallLetterSpacing: Float get() = typography.displaySmallLetterSpacing

    @get:JvmName("typographyHeadlineLargeSize")
    val typographyHeadlineLargeSize: Float get() = typography.headlineLargeSize

    @get:JvmName("typographyHeadlineLargeLineHeight")
    val typographyHeadlineLargeLineHeight: Float get() = typography.headlineLargeLineHeight

    @get:JvmName("typographyHeadlineLargeLetterSpacing")
    val typographyHeadlineLargeLetterSpacing: Float get() = typography.headlineLargeLetterSpacing

    @get:JvmName("typographyHeadlineMediumSize")
    val typographyHeadlineMediumSize: Float get() = typography.headlineMediumSize

    @get:JvmName("typographyHeadlineMediumLineHeight")
    val typographyHeadlineMediumLineHeight: Float get() = typography.headlineMediumLineHeight

    @get:JvmName("typographyHeadlineMediumLetterSpacing")
    val typographyHeadlineMediumLetterSpacing: Float get() = typography.headlineMediumLetterSpacing

    @get:JvmName("typographyHeadlineSmallSize")
    val typographyHeadlineSmallSize: Float get() = typography.headlineSmallSize

    @get:JvmName("typographyHeadlineSmallLineHeight")
    val typographyHeadlineSmallLineHeight: Float get() = typography.headlineSmallLineHeight

    @get:JvmName("typographyHeadlineSmallLetterSpacing")
    val typographyHeadlineSmallLetterSpacing: Float get() = typography.headlineSmallLetterSpacing

    @get:JvmName("typographyTitleLargeSize")
    val typographyTitleLargeSize: Float get() = typography.titleLargeSize

    @get:JvmName("typographyTitleLargeLineHeight")
    val typographyTitleLargeLineHeight: Float get() = typography.titleLargeLineHeight

    @get:JvmName("typographyTitleLargeLetterSpacing")
    val typographyTitleLargeLetterSpacing: Float get() = typography.titleLargeLetterSpacing

    @get:JvmName("typographyTitleMediumSize")
    val typographyTitleMediumSize: Float get() = typography.titleMediumSize

    @get:JvmName("typographyTitleMediumLineHeight")
    val typographyTitleMediumLineHeight: Float get() = typography.titleMediumLineHeight

    @get:JvmName("typographyTitleMediumLetterSpacing")
    val typographyTitleMediumLetterSpacing: Float get() = typography.titleMediumLetterSpacing

    @get:JvmName("typographyTitleSmallSize")
    val typographyTitleSmallSize: Float get() = typography.titleSmallSize

    @get:JvmName("typographyTitleSmallLineHeight")
    val typographyTitleSmallLineHeight: Float get() = typography.titleSmallLineHeight

    @get:JvmName("typographyTitleSmallLetterSpacing")
    val typographyTitleSmallLetterSpacing: Float get() = typography.titleSmallLetterSpacing

    @get:JvmName("typographyBodyLargeSize")
    val typographyBodyLargeSize: Float get() = typography.bodyLargeSize

    @get:JvmName("typographyBodyLargeLineHeight")
    val typographyBodyLargeLineHeight: Float get() = typography.bodyLargeLineHeight

    @get:JvmName("typographyBodyLargeLetterSpacing")
    val typographyBodyLargeLetterSpacing: Float get() = typography.bodyLargeLetterSpacing

    @get:JvmName("typographyBodyMediumSize")
    val typographyBodyMediumSize: Float get() = typography.bodyMediumSize

    @get:JvmName("typographyBodyMediumLineHeight")
    val typographyBodyMediumLineHeight: Float get() = typography.bodyMediumLineHeight

    @get:JvmName("typographyBodyMediumLetterSpacing")
    val typographyBodyMediumLetterSpacing: Float get() = typography.bodyMediumLetterSpacing

    @get:JvmName("typographyBodySmallSize")
    val typographyBodySmallSize: Float get() = typography.bodySmallSize

    @get:JvmName("typographyBodySmallLineHeight")
    val typographyBodySmallLineHeight: Float get() = typography.bodySmallLineHeight

    @get:JvmName("typographyBodySmallLetterSpacing")
    val typographyBodySmallLetterSpacing: Float get() = typography.bodySmallLetterSpacing

    @get:JvmName("typographyLabelLargeSize")
    val typographyLabelLargeSize: Float get() = typography.labelLargeSize

    @get:JvmName("typographyLabelLargeLineHeight")
    val typographyLabelLargeLineHeight: Float get() = typography.labelLargeLineHeight

    @get:JvmName("typographyLabelLargeLetterSpacing")
    val typographyLabelLargeLetterSpacing: Float get() = typography.labelLargeLetterSpacing

    @get:JvmName("typographyLabelMediumSize")
    val typographyLabelMediumSize: Float get() = typography.labelMediumSize

    @get:JvmName("typographyLabelMediumLineHeight")
    val typographyLabelMediumLineHeight: Float get() = typography.labelMediumLineHeight

    @get:JvmName("typographyLabelMediumLetterSpacing")
    val typographyLabelMediumLetterSpacing: Float get() = typography.labelMediumLetterSpacing

    @get:JvmName("typographyLabelSmallSize")
    val typographyLabelSmallSize: Float get() = typography.labelSmallSize

    @get:JvmName("typographyLabelSmallLineHeight")
    val typographyLabelSmallLineHeight: Float get() = typography.labelSmallLineHeight

    @get:JvmName("typographyLabelSmallLetterSpacing")
    val typographyLabelSmallLetterSpacing: Float get() = typography.labelSmallLetterSpacing

    @get:JvmName("fontFamily")
    val fontFamily: String get() = typography.fontFamily

    @get:JvmName("cornerRadiusXSmall")
    val cornerRadiusXSmall: Float get() = shape.cornerRadiusXSmall

    @get:JvmName("cornerRadiusSmall")
    val cornerRadiusSmall: Float get() = shape.cornerRadiusSmall

    @get:JvmName("cornerRadiusMedium")
    val cornerRadiusMedium: Float get() = shape.cornerRadiusMedium

    @get:JvmName("cornerRadiusLarge")
    val cornerRadiusLarge: Float get() = shape.cornerRadiusLarge

    @get:JvmName("cornerRadiusExtraLarge")
    val cornerRadiusExtraLarge: Float get() = shape.cornerRadiusExtraLarge

    @get:JvmName("cornerRadiusFull")
    val cornerRadiusFull: Float get() = shape.cornerRadiusFull

    @get:JvmName("borderWidth")
    val borderWidth: Float get() = geometry.borderWidth

    @get:JvmName("sliderTrackHeight")
    val sliderTrackHeight: Float get() = geometry.sliderTrackHeight

    @get:JvmName("sliderThumbSize")
    val sliderThumbSize: Float get() = geometry.sliderThumbSize

    @get:JvmName("stateLayerSize")
    val stateLayerSize: Float get() = geometry.stateLayerSize

    @get:JvmName("textPaddingX")
    val textPaddingX: Float get() = geometry.textPaddingX

    @get:JvmName("glowColor")
    val glowColor: Int get() = decoration.glowColor

    @get:JvmName("glowIntensity")
    val glowIntensity: Float get() = decoration.glowIntensity

    @get:JvmName("glowSize")
    val glowSize: Float get() = decoration.glowSize

    @get:JvmName("frameStrokeColor")
    val frameStrokeColor: Int get() = decoration.frameStrokeColor

    @get:JvmName("frameStrokeWidth")
    val frameStrokeWidth: Float get() = decoration.frameStrokeWidth

    @get:JvmName("scanlineColor")
    val scanlineColor: Int get() = decoration.scanlineColor

    @get:JvmName("scanlineDensity")
    val scanlineDensity: Float get() = decoration.scanlineDensity

    @get:JvmName("animDurationEnterMs")
    val animDurationEnterMs: Long get() = decoration.animDurationEnterMs

    @get:JvmName("animDurationExitMs")
    val animDurationExitMs: Long get() = decoration.animDurationExitMs

    @get:JvmName("animDurationStaggerMs")
    val animDurationStaggerMs: Long get() = decoration.animDurationStaggerMs
}

@Suppress("MagicNumber", "LongParameterList")
data class ColorTokens(
    val primary: Int = 0,
    val onPrimary: Int = 0,
    val primaryContainer: Int = 0,
    val onPrimaryContainer: Int = 0,
    val inversePrimary: Int = 0,
    val secondary: Int = 0,
    val onSecondary: Int = 0,
    val secondaryContainer: Int = 0,
    val onSecondaryContainer: Int = 0,
    val tertiary: Int = 0,
    val onTertiary: Int = 0,
    val tertiaryContainer: Int = 0,
    val onTertiaryContainer: Int = 0,
    val error: Int = 0,
    val onError: Int = 0,
    val errorContainer: Int = 0,
    val onErrorContainer: Int = 0,
    val successColor: Int = 0,
    val onSuccess: Int = 0,
    val warningColor: Int = 0,
    val onWarning: Int = 0,
    val infoColor: Int = 0,
    val onInfo: Int = 0,
    val background: Int = 0,
    val onBackground: Int = 0,
    val surface: Int = 0,
    val onSurface: Int = 0,
    val surfaceVariant: Int = 0,
    val onSurfaceVariant: Int = 0,
    val surfaceContainerLowest: Int = 0,
    val surfaceContainerLow: Int = 0,
    val surfaceContainer: Int = 0,
    val surfaceContainerHigh: Int = 0,
    val surfaceContainerHighest: Int = 0,
    val surfaceTint: Int = 0,
    val inverseSurface: Int = 0,
    val inverseOnSurface: Int = 0,
    val outline: Int = 0,
    val outlineVariant: Int = 0,
    val shadow: Int = 0,
    val scrim: Int = 0,
)

@Suppress("MagicNumber")
data class StateLayerTokens(
    val hoverOpacity: Float = 0.08f,
    val focusOpacity: Float = 0.10f,
    val pressedOpacity: Float = 0.10f,
    val draggedOpacity: Float = 0.12f,
    val disabledOpacity: Float = 0.38f,
    val disabledContentOpacity: Float = 0.38f,
)

@Suppress("MagicNumber", "LongParameterList")
data class TypographyTokens(
    val displayLargeSize: Float = 9f,
    val displayLargeLineHeight: Float = 9f,
    val displayLargeLetterSpacing: Float = 0f,
    val displayMediumSize: Float = 9f,
    val displayMediumLineHeight: Float = 9f,
    val displayMediumLetterSpacing: Float = 0f,
    val displaySmallSize: Float = 9f,
    val displaySmallLineHeight: Float = 9f,
    val displaySmallLetterSpacing: Float = 0f,
    val headlineLargeSize: Float = 9f,
    val headlineLargeLineHeight: Float = 9f,
    val headlineLargeLetterSpacing: Float = 0f,
    val headlineMediumSize: Float = 9f,
    val headlineMediumLineHeight: Float = 9f,
    val headlineMediumLetterSpacing: Float = 0f,
    val headlineSmallSize: Float = 9f,
    val headlineSmallLineHeight: Float = 9f,
    val headlineSmallLetterSpacing: Float = 0f,
    val titleLargeSize: Float = 9f,
    val titleLargeLineHeight: Float = 9f,
    val titleLargeLetterSpacing: Float = 0f,
    val titleMediumSize: Float = 9f,
    val titleMediumLineHeight: Float = 9f,
    val titleMediumLetterSpacing: Float = 0f,
    val titleSmallSize: Float = 9f,
    val titleSmallLineHeight: Float = 9f,
    val titleSmallLetterSpacing: Float = 0f,
    val bodyLargeSize: Float = 9f,
    val bodyLargeLineHeight: Float = 9f,
    val bodyLargeLetterSpacing: Float = 0f,
    val bodyMediumSize: Float = 9f,
    val bodyMediumLineHeight: Float = 9f,
    val bodyMediumLetterSpacing: Float = 0f,
    val bodySmallSize: Float = 9f,
    val bodySmallLineHeight: Float = 9f,
    val bodySmallLetterSpacing: Float = 0f,
    val labelLargeSize: Float = 9f,
    val labelLargeLineHeight: Float = 9f,
    val labelLargeLetterSpacing: Float = 0f,
    val labelMediumSize: Float = 9f,
    val labelMediumLineHeight: Float = 9f,
    val labelMediumLetterSpacing: Float = 0f,
    val labelSmallSize: Float = 9f,
    val labelSmallLineHeight: Float = 9f,
    val labelSmallLetterSpacing: Float = 0f,
    val fontFamily: String = FontStyle.DEFAULT_NAME,
)

@Suppress("MagicNumber")
data class ShapeTokens(
    val cornerRadiusXSmall: Float = 0f,
    val cornerRadiusSmall: Float = 0f,
    val cornerRadiusMedium: Float = 0f,
    val cornerRadiusLarge: Float = 0f,
    val cornerRadiusExtraLarge: Float = 0f,
    val cornerRadiusFull: Float = 0f,
)

@Suppress("MagicNumber")
data class SharedGeometryTokens(
    val borderWidth: Float = 1f,
    val sliderTrackHeight: Float = 4f,
    val sliderThumbSize: Float = 12f,
    val stateLayerSize: Float = 20f,
    val textPaddingX: Float = 6f,
)

@Suppress("MagicNumber")
data class DecorationTokens(
    val glowColor: Int = 0x00000000,
    val glowIntensity: Float = 0f,
    val glowSize: Float = 0f,
    val frameStrokeColor: Int = 0x00000000,
    val frameStrokeWidth: Float = 0f,
    val scanlineColor: Int = 0x00000000,
    val scanlineDensity: Float = 0f,
    val animDurationEnterMs: Long = 0L,
    val animDurationExitMs: Long = 0L,
    val animDurationStaggerMs: Long = 0L,
)
